import React, { useState, memo } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { ProgramDetailModal } from '../components/ui/ProgramDetailModal';
import { CheckIcon, DumbbellIcon, UsersIcon, TrendingUpIcon, HeartIcon, AwardIcon, DollarSignIcon, CalendarIcon, MapPinIcon, StarIcon, VideoIcon, BuildingIcon, GlobeIcon, ClipboardCheckIcon, BookOpenIcon, HandshakeIcon, SparklesIcon } from 'lucide-react';
export function TrainerPartnerProgram() {
  const [activePersona, setActivePersona] = useState('inperson');
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const personas = [{
    id: 'inperson',
    title: 'In-Person Trainers',
    icon: <DumbbellIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1647" />,
    description: 'Personal trainers and independent fitness coaches'
  }, {
    id: 'virtual',
    title: 'Virtual Trainers',
    icon: <VideoIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1648" />,
    description: 'Online coaches and digital fitness professionals'
  }, {
    id: 'influencers',
    title: 'Wellness Influencers',
    icon: <SparklesIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1649" />,
    description: 'Content creators and wellness advocates'
  }, {
    id: 'clubs',
    title: 'Health Clubs',
    icon: <BuildingIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1650" />,
    description: 'Gyms, studios, and fitness facilities'
  }];
  const personaContent = {
    inperson: {
      title: 'In-Person Personal Trainers',
      subtitle: 'Elevate Your Training Business with Destination Experiences',
      benefits: [{
        title: 'Increase Client Lifetime Value',
        description: 'Transform one-time training sessions into high-value retreat experiences that boost retention and referrals.',
        icon: <TrendingUpIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1651" />
      }, {
        title: 'Build Your Premium Brand',
        description: 'Position yourself as a premium trainer who offers transformative experiences, not just workout sessions.',
        icon: <AwardIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1652" />
      }, {
        title: 'Group Training Opportunities',
        description: 'Bring multiple clients together for group retreats, increasing your efficiency and earning potential.',
        icon: <UsersIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1653" />
      }, {
        title: 'Professional Development',
        description: 'Learn from expert trainers and wellness professionals while experiencing our programs firsthand.',
        icon: <BookOpenIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1654" />
      }],
      challenges: ['Limited earning potential with hourly training', 'Client retention and engagement challenges', 'Competing with big-box gym offerings', 'Seasonal business fluctuations'],
      solutions: ['High-value retreat packages', 'Exclusive client experiences', 'Premium positioning opportunity', 'Year-round booking opportunities']
    },
    virtual: {
      title: 'Virtual Trainers & Online Coaches',
      subtitle: 'Transform Your Digital Community into Real-World Results',
      benefits: [{
        title: 'Deepen Client Relationships',
        description: 'Take your virtual coaching relationship offline with immersive wellness experiences that strengthen bonds and increase retention.',
        icon: <HeartIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1655" />
      }, {
        title: 'Differentiate Your Offering',
        description: 'Stand out in the crowded online fitness space by offering exclusive retreat experiences your competitors cannot match.',
        icon: <StarIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1656" />
      }, {
        title: 'Generate Additional Revenue',
        description: 'Earn competitive commissions on every client booking while providing exceptional value to your community.',
        icon: <DollarSignIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1657" />
      }, {
        title: 'Zero Planning Burden',
        description: 'We handle all logistics, programming, and execution. You focus on your clients while we deliver the experience.',
        icon: <ClipboardCheckIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1658" />
      }],
      challenges: ['Building deeper connections with virtual-only clients', 'Differentiating from other online trainers', 'Creating memorable experiences beyond the screen', 'Generating passive income streams'],
      solutions: ['Exclusive in-person retreat experiences', 'Unique value proposition for your brand', 'Turnkey program management', 'Competitive commission structure']
    },
    influencers: {
      title: 'Wellness Influencers & Content Creators',
      subtitle: 'Monetize Your Influence with Exclusive Wellness Experiences',
      benefits: [{
        title: 'Authentic Brand Partnerships',
        description: 'Offer your audience genuine, transformative experiences that align with your wellness values and strengthen community trust.',
        icon: <HeartIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1659" />
      }, {
        title: 'Premium Content Opportunities',
        description: 'Create compelling content from stunning locations while providing real value to your followers through exclusive retreat experiences.',
        icon: <SparklesIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1660" />
      }, {
        title: 'Diversified Revenue Streams',
        description: 'Generate income beyond sponsorships and ads with high-ticket retreat offerings and attractive commission structures.',
        icon: <DollarSignIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1661" />
      }, {
        title: 'Community Building',
        description: 'Bring your online community together for in-person connections that deepen engagement and loyalty to your brand.',
        icon: <UsersIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1662" />
      }],
      challenges: ['Monetizing influence beyond traditional sponsorships', 'Creating meaningful connections with followers', 'Standing out in saturated wellness space', 'Balancing content creation with business growth'],
      solutions: ['High-value experiential offerings', 'In-person community experiences', 'Exclusive partnership opportunities', 'Turnkey retreat management']
    },
    clubs: {
      title: 'Health Clubs & Fitness Studios',
      subtitle: 'Enhance Member Value with Exclusive Wellness Retreats',
      benefits: [{
        title: 'Member Retention Tool',
        description: 'Offer exclusive retreat experiences that increase member loyalty and reduce churn rates.',
        icon: <HeartIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1663" />
      }, {
        title: 'Premium Membership Tier',
        description: 'Create a high-value membership tier that includes access to annual wellness retreats.',
        icon: <StarIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1664" />
      }, {
        title: 'Staff Incentive Program',
        description: 'Reward top-performing trainers and staff with complimentary retreat attendance.',
        icon: <AwardIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1665" />
      }, {
        title: 'Corporate Partnership Opportunities',
        description: 'Package retreats as corporate wellness offerings for your business clients.',
        icon: <HandshakeIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1666" />
      }],
      challenges: ['Member retention in competitive markets', 'Differentiating from other local clubs', 'Maximizing membership revenue', 'Staff motivation and development'],
      solutions: ['Exclusive member benefits', 'Unique value proposition', 'Additional revenue streams', 'Staff recognition programs']
    }
  };
  // Featured programs from ComebackPrograms
  const programs = [{
    id: 'boxing-bootcamp',
    title: 'Boxing & Fitness Bootcamp with Chris Clements',
    category: 'Fitness',
    ideal: 'Athletes, fitness enthusiasts, boxing beginners',
    description: 'Train like a champion with professional boxer Chris Clements in an intensive boxing and fitness bootcamp designed to push your limits and transform your mindset.',
    duration: '3 days, 2 nights',
    price: '$1,599 per person (double occupancy)',
    priceValue: 1599,
    groupSize: '15-25 participants',
    dates: ['Jan 26-28, 2026'],
    location: 'Hard Rock Hotel Cancun, Mexico',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9avHThGYydRCkXZU4SoNUQ/Boxing.webp',
    hostImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9jNa6v9iwd3jG5mHFF8SXo/Chris2.jpg',
    hostName: 'Chris Clements',
    hostBio: 'Professional boxer and fitness coach with over 15 years of experience training athletes at all levels.',
    highlights: ['Daily boxing training sessions with Chris Clements', 'High-intensity interval training (HIIT) workouts', 'Nutrition and recovery workshops', 'Mental toughness and mindset coaching', 'All-inclusive Hard Rock Hotel accommodations (double occupancy)', 'Group sparring and technique refinement', 'Airport transfers included', 'Flights handled by participants - use your airline points and travel in style']
  }, {
    id: 'wellness-reset',
    title: 'Wellness Reset: Yoga & Breathwork',
    category: 'Wellness',
    ideal: 'Wellness seekers, yoga practitioners, stress relief',
    description: 'Rejuvenate your mind, body, and spirit with a transformative wellness retreat focused on yoga and breathwork practices.',
    duration: '6 days, 5 nights',
    price: '$2,299 per person (double occupancy)',
    priceValue: 2299,
    groupSize: '15-20 participants',
    dates: ['Jan 15-20, 2026', 'April 10-15, 2026', 'Oct 5-10, 2026'],
    location: 'Tulum, Mexico',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/o1fjDw612HCk6Xa8RMhNyh/Yoga.webp',
    hostImage: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=987&q=80',
    hostName: 'Maria Rodriguez',
    hostBio: 'Certified yoga instructor and breathwork specialist with 10+ years of experience guiding transformative wellness retreats.',
    highlights: ['Daily yoga sessions with expert instructors', 'Breathwork and meditation workshops', 'Nutritional counseling and wellness consultations', 'Beachfront sunrise yoga sessions', 'Eco-friendly resort accommodations (double occupancy)', 'Healthy gourmet meals included', 'Airport transfers included', 'Flights handled by participants - use your airline points and travel in style']
  }, {
    id: 'nutrition-wellness',
    title: 'Nutrition & Wellness Transformation',
    category: 'Wellness',
    ideal: 'Health-conscious individuals, nutrition enthusiasts',
    description: 'Transform your relationship with food and discover sustainable nutrition strategies with expert guidance in a stunning beach setting.',
    duration: '5 days, 4 nights',
    price: '$2,499 per person (double occupancy)',
    priceValue: 2499,
    groupSize: '15-20 participants',
    dates: ['March 8-12, 2026', 'Sept 20-24, 2026', 'Nov 15-19, 2026'],
    location: 'Tulum, Mexico',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/aEgUSDVN3SaRCTgZHE6TX2/Nutrition.webp',
    hostImage: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
    hostName: 'Dr. Sarah Chen',
    hostBio: 'Registered dietitian and certified nutrition specialist with expertise in sustainable lifestyle transformation and mindful eating practices.',
    highlights: ['Personalized nutrition assessments', 'Cooking workshops with healthy recipes', 'Mindful eating practices and workshops', 'Farm-to-table dining experiences', 'Luxury boutique hotel accommodations (double occupancy)', 'Meal planning and prep strategies', 'Grocery shopping guide and resources', 'Airport transfers included', 'Flights handled by participants - use your airline points and travel in style']
  }];
  const commissionStructure = [{
    tier: 'Bronze Partner',
    bookings: '1-5 clients/year',
    commission: '7%',
    benefits: ['Marketing materials', 'Program access', 'Partner portal']
  }, {
    tier: 'Silver Partner',
    bookings: '6-15 clients/year',
    commission: '10%',
    benefits: ['All Bronze benefits', 'Co-branded materials', 'Priority booking', 'Quarterly check-ins']
  }, {
    tier: 'Gold Partner',
    bookings: '16+ clients/year',
    commission: '15%',
    benefits: ['All Silver benefits', 'Custom program design', 'Complimentary familiarization trip', 'Dedicated account manager']
  }];
  const steps = [{
    number: '1',
    title: 'Apply',
    description: 'Submit your application with your training credentials and client base information.'
  }, {
    number: '2',
    title: 'Review',
    description: 'Our team reviews your application and schedules a discovery call within 48 hours.'
  }, {
    number: '3',
    title: 'Onboard',
    description: 'Complete partner training and receive your marketing materials and booking portal access.'
  }, {
    number: '4',
    title: 'Launch',
    description: 'Start promoting programs to your clients and earn commissions on every booking.'
  }];
  const handleProgramToggle = programId => {
    setExpandedProgram(expandedProgram === programId ? null : programId);
  };
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1667">
      {/* Hero */}
      <section className="relative py-32 md:py-40 overflow-hidden" data-id="element-1668">
        <div className="absolute inset-0 z-0" data-id="element-1669">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1bXGSRJ9W7VoLhVjbdhEWf/HIIT.webp" alt="People exercising on a Cancun beach" className="w-full h-full object-cover" data-id="element-1670" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0099A1]/90 to-[#374047]/70" data-id="element-1671"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1672">
          <div className="max-w-3xl" data-id="element-1673">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-md" data-id="element-1674">
              Trainer Partner Program
            </h1>
            <p className="text-xl text-white/90 mb-8 drop-shadow-md" data-id="element-1675">
              Transform your training business by offering exclusive wellness
              retreats in Cancun. Earn competitive commissions while providing
              unforgettable experiences to your clients.
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-1676">
              <Button variant="primary" size="lg" href="https://calendar.app.google/4vusYaEgW5mhzbSV9" target="_blank" data-id="element-1677">
                Apply to Partner
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Persona Selector */}
      <section className="py-16 bg-white" data-id="element-1678">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1679">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1680">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1681">
              Choose Your Partner Type
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1682">
              Select your role to see how the Trainer Partner Program works for
              you
            </p>
          </div>
          {/* Persona Tabs */}
          <div className="flex justify-center mb-12 overflow-x-auto" data-id="element-1683">
            <div className="inline-flex space-x-4 bg-[#F4E8D0]/30 p-2 rounded-lg" data-id="element-1684">
              {personas.map(persona => <button key={persona.id} onClick={() => setActivePersona(persona.id)} className={`flex flex-col items-center px-6 py-4 rounded-lg transition-colors ${activePersona === persona.id ? 'bg-[#0099A1] text-white' : 'text-[#374047] hover:bg-[#0099A1]/10'}`} data-id="element-1685">
                  <div className={`mb-2 ${activePersona === persona.id ? 'text-white' : ''}`} data-id="element-1686">
                    {persona.icon}
                  </div>
                  <span className="font-medium whitespace-nowrap" data-id="element-1687">
                    {persona.title}
                  </span>
                  <span className="text-xs mt-1 opacity-80" data-id="element-1688">
                    {persona.description}
                  </span>
                </button>)}
            </div>
          </div>
          {/* Active Persona Content */}
          <div className="max-w-5xl mx-auto" data-id="element-1689">
            <div className="text-center mb-12" data-id="element-1690">
              <h3 className="text-2xl md:text-3xl font-bold text-[#374047] mb-2" data-id="element-1691">
                {personaContent[activePersona].title}
              </h3>
              <p className="text-lg text-[#374047]/80" data-id="element-1692">
                {personaContent[activePersona].subtitle}
              </p>
            </div>
            {/* Benefits Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-1693">
              {personaContent[activePersona].benefits.map((benefit, index) => <Card key={index} data-id="element-1694">
                  <CardBody className="p-6" data-id="element-1695">
                    <div className="flex items-start" data-id="element-1696">
                      <div className="flex-shrink-0 mr-4" data-id="element-1697">{benefit.icon}</div>
                      <div data-id="element-1698">
                        <h4 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1699">
                          {benefit.title}
                        </h4>
                        <p className="text-[#374047]/80" data-id="element-1700">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>)}
            </div>
            {/* Challenges vs Solutions */}
            <div className="grid md:grid-cols-2 gap-8" data-id="element-1701">
              <div data-id="element-1702">
                <h4 className="text-xl font-bold text-[#374047] mb-4" data-id="element-1703">
                  Common Challenges
                </h4>
                <div className="space-y-3" data-id="element-1704">
                  {personaContent[activePersona].challenges.map((challenge, index) => <div key={index} className="flex items-start bg-[#F4E8D0]/30 p-3 rounded-lg" data-id="element-1705">
                        <span className="text-[#FF6B65] mr-3" data-id="element-1706">✗</span>
                        <span className="text-[#374047]" data-id="element-1707">{challenge}</span>
                      </div>)}
                </div>
              </div>
              <div data-id="element-1708">
                <h4 className="text-xl font-bold text-[#374047] mb-4" data-id="element-1709">
                  Our Solutions
                </h4>
                <div className="space-y-3" data-id="element-1710">
                  {personaContent[activePersona].solutions.map((solution, index) => <div key={index} className="flex items-start bg-[#0099A1]/10 p-3 rounded-lg" data-id="element-1711">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] mr-3 flex-shrink-0 mt-0.5" data-id="element-1712" />
                        <span className="text-[#374047]" data-id="element-1713">{solution}</span>
                      </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Featured Programs */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-1714">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1715">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1716">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1717">
              Featured Wellness Programs
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1718">
              Choose from our signature programs or work with us to create
              custom experiences for your clients
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-1719">
            {programs.map((program, index) => <Card key={index} className="overflow-hidden" data-id="element-1720">
                <div className="h-64 overflow-hidden" data-id="element-1721">
                  <img src={program.image} alt={program.title} className="w-full h-full object-cover" data-id="element-1722" />
                </div>
                <CardBody className="p-6" data-id="element-1723">
                  <div className="mb-2" data-id="element-1724">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1]" data-id="element-1725">
                      {program.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#374047] mb-2" data-id="element-1726">
                    {program.title}
                  </h3>
                  <div className="space-y-2 mb-4" data-id="element-1727">
                    <div className="flex items-center text-sm text-[#374047]/70" data-id="element-1728">
                      <CalendarIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-1729" />
                      {program.duration}
                    </div>
                    <div className="flex items-center text-sm text-[#374047]/70" data-id="element-1730">
                      <UsersIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-1731" />
                      {program.ideal}
                    </div>
                    <div className="flex items-center text-sm text-[#374047]/70" data-id="element-1732">
                      <DollarSignIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-1733" />
                      {program.price}
                    </div>
                  </div>
                  {/* Expandable Details */}
                  {expandedProgram === program.id && <div className="mt-4 pt-4 border-t border-[#374047]/10 space-y-4" data-id="element-1734">
                      <div data-id="element-1735">
                        <p className="text-sm text-[#374047]/80 mb-4" data-id="element-1736">
                          {program.description}
                        </p>
                      </div>
                      <div data-id="element-1737">
                        <h4 className="text-sm font-semibold text-[#374047] mb-2" data-id="element-1738">
                          Location
                        </h4>
                        <div className="flex items-center text-sm text-[#374047]/80" data-id="element-1739">
                          <MapPinIcon className="h-4 w-4 mr-2 text-[#0099A1]" data-id="element-1740" />
                          {program.location}
                        </div>
                      </div>
                      <div data-id="element-1741">
                        <h4 className="text-sm font-semibold text-[#374047] mb-2" data-id="element-1742">
                          Upcoming Dates
                        </h4>
                        <div className="flex flex-wrap gap-2" data-id="element-1743">
                          {program.dates.map((date, i) => <span key={i} className="text-xs bg-[#0099A1]/10 text-[#0099A1] px-2 py-1 rounded" data-id="element-1744">
                              {date}
                            </span>)}
                        </div>
                      </div>
                      <div data-id="element-1745">
                        <h4 className="text-sm font-semibold text-[#374047] mb-2" data-id="element-1746">
                          Program Highlights
                        </h4>
                        <ul className="space-y-1" data-id="element-1747">
                          {program.highlights.map((highlight, i) => <li key={i} className="flex items-start text-sm text-[#374047]/80" data-id="element-1748">
                              <CheckIcon className="h-4 w-4 text-[#0099A1] mr-2 flex-shrink-0 mt-0.5" data-id="element-1749" />
                              <span data-id="element-1750">{highlight}</span>
                            </li>)}
                        </ul>
                      </div>
                      {program.hostImage && program.hostName && <div className="bg-[#F4E8D0]/30 p-3 rounded-lg" data-id="element-1751">
                          <h4 className="text-sm font-semibold text-[#374047] mb-2" data-id="element-1752">
                            Your Host
                          </h4>
                          <div className="flex items-start" data-id="element-1753">
                            <img src={program.hostImage} alt={program.hostName} className="w-12 h-12 rounded-full object-cover mr-3 flex-shrink-0" data-id="element-1754" />
                            <div data-id="element-1755">
                              <p className="font-semibold text-sm text-[#374047]" data-id="element-1756">
                                {program.hostName}
                              </p>
                              {program.hostBio && <p className="text-xs text-[#374047]/80 mt-1" data-id="element-1757">
                                  {program.hostBio}
                                </p>}
                            </div>
                          </div>
                        </div>}
                      <Button variant="primary" className="w-full" href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0LqS_WBaJU_UweOzewNSfk2vu7lxzFJN3nlB21e-yByPHLTO2p6nMafIxTmsxJzJbdXaw9qtoL" target="_blank" data-id="element-1758">
                        Book Appointment to Partner with Us
                      </Button>
                    </div>}
                  <Button variant="outline" className="w-full mt-4" onClick={() => handleProgramToggle(program.id)} data-id="element-1759">
                    {expandedProgram === program.id ? 'Show Less' : 'View Details'}
                  </Button>
                </CardBody>
              </Card>)}
          </div>
          <div className="text-center mt-8" data-id="element-1760">
            <Button variant="primary" href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0LqS_WBaJU_UweOzewNSfk2vu7lxzFJN3nlB21e-yByPHLTO2p6nMafIxTmsxJzJbdXaw9qtoL" target="_blank" data-id="element-1761">
              Book Appointment to Partner with Us
            </Button>
          </div>
        </div>
      </section>
      {/* Commission Structure */}
      <section className="py-16 bg-white" data-id="element-1762">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1763">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1764">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1765">
              Competitive Commission Structure
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1766">
              Earn more as you grow. Our tiered structure rewards your success.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-1767">
            {commissionStructure.map((tier, index) => <Card key={index} className={`${index === 1 ? 'border-2 border-[#0099A1]' : ''}`} data-id="element-1768">
                <CardHeader className={`${index === 1 ? 'bg-[#0099A1] text-white' : 'bg-[#F4E8D0]/30'} p-6`} data-id="element-1769">
                  {index === 1 && <div className="text-center mb-2" data-id="element-1770">
                      <span className="inline-block bg-[#FF6B65] text-white text-xs px-3 py-1 rounded-full" data-id="element-1771">
                        Most Popular
                      </span>
                    </div>}
                  <h3 className={`text-2xl font-bold ${index === 1 ? 'text-white' : 'text-[#374047]'} text-center`} data-id="element-1772">
                    {tier.tier}
                  </h3>
                  <p className={`text-center ${index === 1 ? 'text-white/80' : 'text-[#374047]/70'} mt-2`} data-id="element-1773">
                    {tier.bookings}
                  </p>
                  <div className="text-center mt-4" data-id="element-1774">
                    <span className={`text-4xl font-bold ${index === 1 ? 'text-white' : 'text-[#0099A1]'}`} data-id="element-1775">
                      {tier.commission}
                    </span>
                    <span className={`${index === 1 ? 'text-white/80' : 'text-[#374047]/70'}`} data-id="element-1776">
                      {' '}
                      commission
                    </span>
                  </div>
                </CardHeader>
                <CardBody className="p-6" data-id="element-1777">
                  <ul className="space-y-3" data-id="element-1778">
                    {tier.benefits.map((benefit, i) => <li key={i} className="flex items-start" data-id="element-1779">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5 mr-2" data-id="element-1780" />
                        <span className="text-[#374047]" data-id="element-1781">{benefit}</span>
                      </li>)}
                  </ul>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-1782">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1783">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1784">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1785">
              How It Works
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1786">
              Get started in four simple steps
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto" data-id="element-1787">
            {steps.map((step, index) => <div key={index} className="text-center" data-id="element-1788">
                <div className="w-16 h-16 bg-[#0099A1] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4" data-id="element-1789">
                  {step.number}
                </div>
                <h3 className="text-xl font-bold text-[#374047] mb-2" data-id="element-1790">
                  {step.title}
                </h3>
                <p className="text-[#374047]/80" data-id="element-1791">{step.description}</p>
              </div>)}
          </div>
        </div>
      </section>
      {/* Partner Benefits */}
      <section className="py-16 bg-white" data-id="element-1792">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1793">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1794">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1795">
              Why Partner With DestaMeet?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-1796">
            <div className="text-center" data-id="element-1797">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1798">
                <ClipboardCheckIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1799" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1800">
                Turnkey Operations
              </h3>
              <p className="text-[#374047]/80" data-id="element-1801">
                We handle all logistics, programming, and execution. You focus
                on your clients.
              </p>
            </div>
            <div className="text-center" data-id="element-1802">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1803">
                <DollarSignIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1804" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1805">
                Competitive Commissions
              </h3>
              <p className="text-[#374047]/80" data-id="element-1806">
                Earn up to 20% commission on all bookings with our tiered
                structure.
              </p>
            </div>
            <div className="text-center" data-id="element-1807">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1808">
                <StarIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1809" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1810">
                Premium Experiences
              </h3>
              <p className="text-[#374047]/80" data-id="element-1811">
                Offer world-class wellness programs at luxury resorts in Cancun.
              </p>
            </div>
            <div className="text-center" data-id="element-1812">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1813">
                <HandshakeIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1814" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1815">
                Dedicated Support
              </h3>
              <p className="text-[#374047]/80" data-id="element-1816">
                Your own account manager to help you succeed every step of the
                way.
              </p>
            </div>
            <div className="text-center" data-id="element-1817">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1818">
                <BookOpenIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1819" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1820">
                Marketing Resources
              </h3>
              <p className="text-[#374047]/80" data-id="element-1821">
                Professional marketing materials and co-branded content to
                promote programs.
              </p>
            </div>
            <div className="text-center" data-id="element-1822">
              <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1823">
                <GlobeIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1824" />
              </div>
              <h3 className="text-lg font-bold text-[#374047] mb-2" data-id="element-1825">
                Partner Portal
              </h3>
              <p className="text-[#374047]/80" data-id="element-1826">
                Easy-to-use booking system and dashboard to track your
                commissions.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 bg-[#0099A1]" data-id="element-1827">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1828">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1829">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1830">
              Ready to Transform Your Training Business?
            </h2>
            <p className="text-xl text-white/90 mb-8" data-id="element-1831">
              Join our network of elite fitness professionals and start offering
              unforgettable wellness experiences to your clients.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1832">
              <Button variant="primary" size="lg" href="https://calendar.app.google/4vusYaEgW5mhzbSV9" target="_blank" data-id="element-1833">
                Apply Now
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white/10" href="https://calendar.app.google/4vusYaEgW5mhzbSV9" target="_blank" data-id="element-1834">
                Schedule a Call
              </Button>
            </div>
            <p className="text-sm text-white/70 mt-6" data-id="element-1835">
              Applications reviewed within 48 hours
            </p>
          </div>
        </div>
      </section>
      {/* Program Detail Modal */}
      {selectedProgram && <ProgramDetailModal program={selectedProgram} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data-id="element-1836" />}
    </div>;
}